import React from "react";
import { ChevronDown, CircleDollarSign, RotateCcw } from "lucide-react";
import { payableInr } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const label = (value) => String(value || "not requested").replaceAll("_", " ");

export const OrderPaymentRefundDetails = ({ order }) => {
  const refund = order.refund_summary;

  if (!refund) {
    return null;
  }

  const needsAttention = ["pending", "failed"].includes(refund.status);
  const paymentLabel = label(order.payment_status);
  const refundLabel = refund.status === "not_required" ? "No refund" : label(refund.status);
  const refundTone = needsAttention
    ? "border-amber-200 bg-[#FEF3C7] text-[#92400E]"
    : "border-emerald-200 bg-[#EBF2EE] text-[#1E4A38]";

  return (
    <span
      className="inline-block font-sans text-xs text-[#1E2A24]"
      data-testid={`admin-refund-details-${order.id}`}
    >
      <Popover>
        <PopoverTrigger asChild>
          <button
            aria-label={`View payment and refund details for order ${order.id}`}
            className="flex items-center gap-1.5"
            data-testid={`admin-refund-details-toggle-${order.id}`}
            type="button"
          >
            <span className="inline-flex items-center gap-1 text-[#1E2A24]">
              <CircleDollarSign size={13} className="text-[#2D5A47]" aria-hidden="true" />
              <span className="font-semibold capitalize" data-testid={`admin-payment-status-${order.id}`}>
                {paymentLabel}
              </span>
            </span>
            <span
              className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${refundTone}`}
              data-testid={`admin-refund-status-${order.id}`}
            >
              {refundLabel}
            </span>
            <ChevronDown className="text-[#64748B]" size={14} aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-72 rounded-none border-[#D9C3A5] bg-white p-4 text-[#1E2A24] shadow-xl"
          data-testid={`admin-refund-popover-${order.id}`}
          sideOffset={8}
        >
          <div className="mb-3 border-b border-[#E2DCD2] pb-2 font-semibold text-[#1E2A24]">
            Payment &amp; refund
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-[11px]">
            <div>
              <dt className="text-[#64748B]">Payment</dt>
              <dd className="capitalize">{paymentLabel}</dd>
            </div>
            <div>
              <dt className="text-[#64748B]">Method</dt>
              <dd className="capitalize" data-testid={`admin-payment-method-${order.id}`}>
                {label(order.payment_method)}
              </dd>
            </div>
            <div>
              <dt className="text-[#64748B]">Refund amount</dt>
              <dd data-testid={`admin-refund-amount-${order.id}`}>
                {refund.amount === null ? "—" : payableInr(refund.amount)}
              </dd>
            </div>
            <div>
              <dt className="text-[#64748B]">Refund ID</dt>
              <dd className="break-all font-mono" data-testid={`admin-refund-id-${order.id}`}>
                {refund.id || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-[#64748B]">Updated</dt>
              <dd data-testid={`admin-refund-updated-at-${order.id}`}>
                {refund.updated_at ? new Date(refund.updated_at).toLocaleString("en-IN") : "—"}
              </dd>
            </div>
          </dl>
          {refund.error && (
            <p
              className="mt-3 flex items-start gap-1.5 text-red-700"
              data-testid={`admin-refund-error-${order.id}`}
            >
              <RotateCcw size={13} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>{refund.error}</span>
            </p>
          )}
        </PopoverContent>
      </Popover>
    </span>
  );
};